import { observer } from "mobx-react";
import {
  CompanySearchDialogState,
  extractPortfolioContactsAdminUrl,
  NotConnectedTabState,
  PortfolioCompaniesTab,
  PortfolioType,
  PreparedPageState,
  ReadyState,
  SelectedPortfolioOption,
  usePortfolio,
} from "../portfolioDomain";
import { CompanyTable } from "../../views/Pages/Portfolio/CompanyTable";
import {
  useDispatch,
  useLanguage,
  useLocaleFormat,
  useRootStore,
  useTranslation,
} from "../../stores/RootStoreContext";
import { BottomSpacer, Box, Container, Grid, VBox } from "../../components-v2/Box";
import { EmptyState } from "../../components-v2/EmptyState";
import { Button, CloseButton, LinkButton } from "../../components-v2/Button";
import { Tabs } from "../../components-v2/Tabs";
import { isMobileView } from "../../utils/screen";
import { MTextInput } from "../../components-v2/TextInput";
import {
  DecisionsByCategory,
  getDecisionsByCategory,
} from "../../dbot/projectList/projectListDomain";
import { isFailure, isOk, isPending, Result } from "../../common/common";
import { Dialog } from "../../components-v2/Dialog";
import { Alert } from "../../components-v2/Alert";
import { Markdown } from "../../text/Markdown";
import {
  ConfirmDissolveDialog,
  InvalidPortfolioView,
  IsEmailVerifiedAlert,
  PortfolioSettingsDialog,
  UserAcceptTerms,
} from "../portfolioComponents";
import { OnboardingCandidateResponse, PortfolioCompany } from "../../stores/PortfolioStore";
import { DecisionType } from "../../common/decisions";
import { Checkbox } from "../../components-v2/Checkbox";
import { DecisionLabel } from "../../dbot/projectList/projectListComponents";
import { isBlank, isNotBlank } from "../../utils/strings";
import { Autocomplete } from "../../components-v2/Autocomplete";
import {
  ActionsForNotConnectedCompanies,
  AddCompanyToPortfolioActions,
  CompanySearchActions,
  filteredUserCompanies,
  hasAdminAccess,
  initPortfolioCompaniesPage,
  NewProjectActions,
  PortfolioPageActions,
  prepareCompaniesWithSubscriptionStatus,
  prepareState,
} from "./companiesDomain";
import { FC, ReactElement, useEffect } from "react";
import { AskForPermissionForm } from "../../companysearch/SearchDialogs";
import { SuggestedCompany } from "../../types/CompanyInfo";
import { SearchResultTableRow } from "../../views/Pages/Portfolio/CompanySearch";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { isPreparedPageState } from "../../signing/signDocumentDomain";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import { useAppBar } from "../../appBar/useAppBar";
import { DropdownMenu, DropdownMenuItemType } from "../../components-v2/Dropdown";
import { FullScreenLayout } from "../../components-v2/FullScreenLayout";
import { AppBar } from "../../components-v2/AppBar";
import { InvitationRequest } from "../../types/Invitation";
import { isProductionEnvironment } from "../../utils/environments";
import { formatPercent } from "../../utils/numbers";
import { TMessage } from "../../i18n/tt";
import { MSelect, Select } from "../../components-v2/Select";
import { AccessType } from "../../types/enums/AccessType";
import { SubscriptionStatus, SubscriptionType } from "../../common/subscription";
import { Icon } from "../../components-v2/Icon";
import { altinnMainDocsUrl } from "../../utils/navigation";
import { openBeneficialOwnersDocumentation } from "../../shareholderRegister/beneficialOwners/beneficialOwnersDomain";
import { SigningCompleteDialog } from "../signatures/signaturesComponents";
import { fromQueryString, toQueryString } from "../../utils/queryParams";
import { dismissAlert, isAlertDismissed, UserPrefs } from "../../stores/userPrefsDomain";
import { getRoleNameByLanguage } from "../contacts/customRoleDomain";
import { UserSettings } from "../../types/UserInfo";

export const PortfolioCompaniesPage = observer(function PortfolioCompaniesInitializer({
  companiesTab,
  ...props
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  companiesTab?: PortfolioCompaniesTab;
}) {
  const { portfolio, onRemovePortfolio, onUpdatePortfolio, header, currentUrl } =
    usePortfolio(props);
  const rootStore = useRootStore();
  const { messageStore, userStore, userPrefs, apiService } = rootStore;
  const dispatch = useDispatch();
  const locale = useLanguage();
  const location = useRootStore().location;
  const queryParams = fromQueryString(location.search);
  const closeSigningCompleteDialog = () => {
    const updatedUrl = `${location.pathname}${toQueryString({
      ...queryParams,
      "signing-complete": undefined,
      "signing-complete-company": undefined,
    })}`;
    dispatch([{ type: "navigate", url: updatedUrl, replace: true }]);
  };
  const companyName: string = queryParams["signing-complete-company"] ?? "";
  const initialShowCompletedSignedDialog = queryParams["signing-complete"] === "true";
  useEffect(() => {
    dispatch(
      initPortfolioCompaniesPage({
        dispatch,
        currentUrl,
        portfolio,
        companiesTab,
        signDocumentCounts: userStore.signDocumentCounts,
        store: rootStore,
        setPortfolioFollowUpCount: userStore.setPortfolioFollowUpCount,
      }),
    );
  }, [
    dispatch,
    portfolio,
    companiesTab,
    header,
    currentUrl,
    rootStore,
    userStore.signDocumentCounts,
    userStore.setPortfolioFollowUpCount,
  ]);

  const { t } = useTranslation();
  const state = prepareState({
    store: rootStore,
    dispatch,
    t,
    portfolio,
    onRemovePortfolio,
    onUpdatePortfolio,
    locale,
    setCustomRoleId: userStore.setCustomRoleId,
    setPortfolioFollowUpCount: userStore.setPortfolioFollowUpCount,
    apiService,
  });
  if (
    isPreparedPageState(state, "ready") &&
    state.acceptedTerms &&
    !state.acceptedTerms.acceptedTerms
  ) {
    return <UserAcceptTerms state={state.acceptedTerms} />;
  }
  return (
    <PortfolioCompaniesView
      state={state}
      alertStore={messageStore}
      showSigningCompleteDialog={initialShowCompletedSignedDialog}
      closeSigningCompleteDialog={closeSigningCompleteDialog}
      companyName={companyName}
      userPrefs={userPrefs}
      userSettings={userStore.userSettings}
      showFollowUpAlert={userStore.hasAnyAdminRole}
      portfolio={portfolio}
    />
  );
});

const CompaniesView = observer(
  ({
    state,
    actions,
    userSettings,
  }: {
    state: ReadyState;
    actions: PortfolioPageActions;
    userSettings: UserSettings | null;
  }) => {
    const hasMultipleCompanies: boolean = state.userCompaniesInPortfolio.length !== 1;
    const hasUserCompaniesInPortfolio =
      (state.userCompaniesInPortfolio.length ?? 0) > 0 || !state.portfolioSearchModel.isEmpty;
    return (
      <>
        {state.portfolio.type === "portfolio-instance" && (
          <Box bg borderBottom dropShadow>
            <Container maxWidth="xl" bg>
              <Tabs tabs={state.companiesTabs} />
            </Container>
          </Box>
        )}
        <Container padding maxWidth="xl">
          <IsEmailVerifiedAlert isEmailVerified={state.portfolioResponse.user.isEmailVerified} />
          <FilterForNotConnectedCompanies state={state} actions={actions} />
          <Box display={"block"}>
            {state.portfolioResponse.companyItems.length > 0 && hasUserCompaniesInPortfolio ? (
              <>
                {state.companiesTab !== "not-connected" && (
                  <SearchAndToolBarCompanies
                    state={state}
                    actions={actions}
                    hasMultipleCompanies={hasMultipleCompanies}
                    includePrimaryActions={
                      state.portfolio.type === "portfolio-instance" &&
                      state.companiesTab !== "ar-status" &&
                      state.companiesTab !== "beneficial-owners"
                    }
                  />
                )}
                <CompaniesInPortfolioTable
                  state={state}
                  actions={actions}
                  userSettings={userSettings}
                />
              </>
            ) : state.portfolio.type !== "portfolio-instance" ? (
              <CompaniesInPortfolioTable
                state={state}
                actions={actions}
                userSettings={userSettings}
              />
            ) : (
              <EmptyPortfolioView
                portfolioName={state.portfolioResponse.portfolio?.name}
                openAddCompanyDialog={actions.addCompanyToPortfolioActions.openAddCompanyDialog}
                openPortfolioSettings={actions.openPortfolioSettings}
                confirmDissolvePortfolio={actions.confirmDissolvePortfolio}
              />
            )}
            {state.isAddCompanyDialogOpen && (
              <AddCompanyToPortfolioDialog
                state={state}
                actions={actions.addCompanyToPortfolioActions}
              />
            )}
            {state.isNewProjectDialogOpen && hasMultipleCompanies && (
              <NewProjectDialog
                state={state}
                actions={actions.newProjectActions}
                companyToPortfolioActions={actions.addCompanyToPortfolioActions}
              />
            )}
            {state.removeCompanyFromPortfolio && (
              <RemoveCompanyDialog state={state} actions={actions} />
            )}
            {state.moveToPortfolio && (
              <MoveCompanyToPortfolioDialog state={state} actions={actions} />
            )}
            {state.openInvitationRequestId && state.openInvitationRequestResult !== undefined && (
              <PendingInvitationDetailsDialog
                invitationRequestResult={state.openInvitationRequestResult}
                onClose={actions.closePendingInvitationDialog}
              />
            )}
          </Box>
        </Container>
      </>
    );
  },
);

export const CompaniesInPortfolioTable = observer(
  ({
    state,
    actions,
    userSettings,
  }: {
    state: ReadyState;
    actions: PortfolioPageActions;
    userSettings: UserSettings | null;
  }) => {
    if (
      state.portfolioResponse.userCompanies.length === 0 &&
      filteredUserCompanies(state).length === 0 &&
      state.companiesTab === "all"
    ) {
      return (
        <AvailableCompaniesEmptyState
          onAddNorwegianCompany={actions.companySearchActions.openNorwegianCompanySearch}
          onAddSwedishCompany={actions.companySearchActions.openSwedishCompanySearch}
        />
      );
    } else if (
      state.portfolioResponse.userCompanies.length === 0 &&
      filteredUserCompanies(state).length === 0 &&
      state.companiesTab === "starred" &&
      state.portfolioSearchModel.isEmpty
    ) {
      return <StarredCompaniesEmptyState />;
    } else if (
      state.notConnected.companies.length === 0 &&
      state.companiesTab === "not-connected" &&
      state.portfolioSearchModel.isEmpty
    ) {
      return (
        <NotConnectedCompaniesEmptyState
          onAddNorwegianCompany={actions.companySearchActions.openNorwegianCompanySearch}
          onAddSwedishCompany={actions.companySearchActions.openSwedishCompanySearch}
        />
      );
    }
    return (
      <CompanyTable
        data={
          state.companiesTab === "not-connected"
            ? state.notConnected.companies
            : filteredUserCompanies(state)
        }
        portfolioActions={actions.portfolioActions}
        isRenderStar={actions.portfolioActions.isRenderStar}
        onSelect={
          state.companiesTab === "beneficial-owners"
            ? actions.portfolioActions.openBeneficialOwnersPage
            : state.companiesTab === "ar-status"
              ? actions.portfolioActions.openARPage
              : actions.portfolioActions.onSelect
        }
        activeTab={state.companiesTab}
        onClickStar={actions.portfolioActions.onClickStar}
        userSettings={userSettings}
      />
    );
  },
);

export function AvailableCompaniesEmptyState({
  onAddNorwegianCompany,
  onAddSwedishCompany,
}: {
  onAddNorwegianCompany: () => void;
  onAddSwedishCompany: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box padding={"lg"}>
      <EmptyState
        illustration={<Icon name={"briefcase"} duotone size={"mega"} />}
        title={t("portfolio.empty-active-companies.title")}
        description={t("portfolio.empty-active-companies.description")}
        actions={[
          <Button
            key={"add-norwegian-company"}
            onClick={onAddNorwegianCompany}
            label={t("portfolio.empty-active-companies.add-norwegian-company")}
            variant={"secondary"}
          />,
          <Button
            key={"add-swedish-company"}
            onClick={onAddSwedishCompany}
            label={t("portfolio.empty-active-companies.add-swedish-company")}
            variant={"secondary"}
          />,
        ]}
      />
      <BottomSpacer />
    </Box>
  );
}

function NotConnectedCompaniesEmptyState({
  onAddNorwegianCompany,
  onAddSwedishCompany,
}: {
  onAddNorwegianCompany: () => void;
  onAddSwedishCompany: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box padding={"lg"}>
      <EmptyState
        illustration={<Icon name={"plugs"} duotone size={"mega"} />}
        title={t("portfolio.empty-not-connected-companies.title")}
        description={t("portfolio.empty-not-connected-companies.description")}
        actions={[
          <Button
            key={"add-norwegian-company"}
            onClick={onAddNorwegianCompany}
            label={t("portfolio.empty-active-companies.add-norwegian-company")}
            variant={"secondary"}
          />,
          <Button
            key={"add-swedish-company"}
            onClick={onAddSwedishCompany}
            label={t("portfolio.empty-active-companies.add-swedish-company")}
            variant={"secondary"}
          />,
        ]}
      />
      <BottomSpacer />
    </Box>
  );
}

function StarredCompaniesEmptyState() {
  const { t } = useTranslation();
  return (
    <Box padding={"lg"}>
      <EmptyState
        illustration={<Icon name={"star"} duotone size={"mega"} />}
        title={t("portfolio.empty-starred-companies.title")}
        description={t("portfolio.empty-starred-companies.description")}
      />
      <BottomSpacer />
    </Box>
  );
}

const SearchAndToolBarCompanies = observer(
  ({
    state,
    actions,
    hasMultipleCompanies,
    includePrimaryActions,
  }: {
    state: ReadyState;
    hasMultipleCompanies: boolean;
    actions: PortfolioPageActions;
    includePrimaryActions: boolean;
  }) => {
    const { t } = useTranslation();

    return (
      <Box justifyContent="space-between" marginBottom vertical={isMobileView()}>
        <Box gap={"md"}>
          <MTextInput
            placeholder={
              state.portfolioResponse.portfolio
                ? t("search_in", { name: state.portfolioResponse.portfolio.name })
                : t("search")
            }
            icon={"magnifying-glass"}
            model={state.portfolioSearchModel}
            onChange={(e) => {
              state.portfolioSearchModel.setInputValue(e);
              actions.portfolioActions.filterWithText();
            }}
            autofocus
            type={"search"}
          />
          <Box gap={"md"}>
            <Select
              {...prepareCompaniesWithSubscriptionStatus(
                state.companySubscription,
                actions.setCompaniesWithSubscriptionStatus,
              )}
            />
            {state.companySubscription !== "all" && (
              <LinkButton
                label={t("showAll")}
                icon={"arrow-counter-clockwise"}
                onClick={() => actions.setCompaniesWithSubscriptionStatus("all")}
              />
            )}
          </Box>
        </Box>
        <Box gap={isMobileView() ? "sm" : "md"} vertical={isMobileView()}>
          {includePrimaryActions && (
            <>
              <Button
                label={t("portfolio.invite-admin")}
                icon={"user-plus"}
                variant={"secondary"}
                onClick={actions.openInviteAdministratorDialog}
              />
              <ManagePortfolioMenu
                state={state}
                actions={actions}
                hasMultipleCompanies={hasMultipleCompanies}
              />
            </>
          )}
        </Box>
        {state.companiesTab === "ar-status" && (
          <Button
            variant={"secondary"}
            icon={"arrow-square-out"}
            label={{ key: "portfolio.ar.documentation" }}
            onClick={openARDocumentation}
          />
        )}
        {state.companiesTab === "beneficial-owners" && (
          <Button
            variant={"secondary"}
            icon={"arrow-square-out"}
            label={{ key: "portfolio.beneficial-owners.documentation" }}
            onClick={openBeneficialOwnersDocumentation}
          />
        )}
      </Box>
    );
  },
);

const ManagePortfolioMenu = observer(
  ({
    state,
    actions,
    hasMultipleCompanies,
  }: {
    state: ReadyState;
    actions: PortfolioPageActions;
    hasMultipleCompanies: boolean;
  }) => {
    const { t } = useTranslation();
    const decisionsByCategory = getDecisionsByCategory({
      isBatchProject: true,
      checkedDecisions: state.checkedDecisions,
      paidSubscription: {
        organisationNumber: "",
        subscriptionStatus: SubscriptionStatus.ACTIVE,
        subscriptionType: SubscriptionType.PRO,
        isActive: true,
      },
    });
    return (
      <DropdownMenu
        button={{
          type: "button",
          variant: "secondary",
          label: t("portfolio.options.menu.manage-companies"),
        }}
        items={[
          hasMultipleCompanies && {
            type: "label",
            label: t("portfolio.options.menu.start-portfolio-project"),
          },

          ...decisionsByCategory.flatMap((category) =>
            category.decisions.map<DropdownMenuItemType | null>((type) =>
              hasMultipleCompanies
                ? {
                    type: "item",
                    label: <DecisionLabel decisionType={type.decisionType} />,
                    icon: "sparkle",
                    onSelect: () => {
                      actions.openCreateProjectDialog(type.decisionType);
                    },
                  }
                : null,
            ),
          ),
          { type: "separator" },
          { type: "label", label: t("portfolio.options.menu.expand-portfolio") },
          {
            type: "item",
            label: t("portfolio.welcome.add-company"),
            icon: "plus",
            onSelect: () => {
              actions.addCompanyToPortfolioActions.openAddCompanyDialog();
            },
          },
          {
            type: "item",
            label: t("portfolio.invite-admin"),
            icon: "user-plus",
            onSelect: () => {
              actions.openInviteAdministratorDialog();
            },
          },
          {
            type: "item",
            label: t("portfolio.export-shareholder-registers"),
            icon: "download",
            onSelect: () => {
              actions.openExportShareholdersDialog();
            },
          },
          {
            type: "item",
            label: t("portfolio.options.menu.rename-portfolio"),
            icon: "edit",
            onSelect: () => {
              actions.openPortfolioSettings();
            },
          },
          {
            type: "item",
            label: t("portfolio.dissolve-portfolio"),
            icon: "trash",
            color: "error",
            onSelect: () => {
              actions.confirmDissolvePortfolio();
            },
          },
        ]}
      />
    );
  },
);

const NewProjectDialog = observer(
  ({
    state,
    actions,
    companyToPortfolioActions,
  }: {
    state: ReadyState;
    actions: NewProjectActions;
    companyToPortfolioActions: AddCompanyToPortfolioActions;
  }) => {
    const { t } = useTranslation();
    const decisionsByCategory = getDecisionsByCategory({
      isBatchProject: true,
      checkedDecisions: state.checkedDecisions,
      paidSubscription: {
        organisationNumber: "",
        subscriptionStatus: SubscriptionStatus.ACTIVE,
        subscriptionType: SubscriptionType.PRO,
        isActive: true,
      },
    });
    return (
      <Dialog
        title={
          state.portfolioResponse.portfolio?.name
            ? t("decisions.what-to-do-in", { in: state.portfolioResponse.portfolio?.name })
            : t("decisions.what-to-do")
        }
        submitLabel={t("continue")}
        isSubmitDisabled={state.checkedDecisions.length === 0}
        isSubmitting={isPending(state.createProjectResult)}
        onSubmit={actions.createBatchProject}
        onCancel={actions.closeNewProjectDialog}
      >
        <DecisionsAndCompaniesList
          decisions={decisionsByCategory}
          toggleDecision={actions.toggleDecision}
          companies={state.userCompaniesInPortfolio}
          action={companyToPortfolioActions}
        />
        <VBox marginTop>
          <Alert
            type={"info"}
            message={
              <div>
                <Markdown
                  value={t("decisions.new-project.work-in-progress", {
                    link: "https://airtable.com/apppdnKJrnENisz3z/shrKU5vSljC5QZs3j?prefill_module=dBot_batch_operations",
                  })}
                />
              </div>
            }
            icon={"info"}
          />
          {isFailure(state.createProjectResult) && (
            <Box justifyContent={"end"}>
              <Alert type="error" message={state.createProjectResult.error} />
            </Box>
          )}
        </VBox>
      </Dialog>
    );
  },
);

const DecisionsAndCompaniesList = observer(
  ({
    decisions,
    companies,
    toggleDecision,
    action,
  }: {
    decisions: DecisionsByCategory[];
    companies: PortfolioCompany[];
    toggleDecision: (decision: DecisionType) => void;
    action?: AddCompanyToPortfolioActions;
  }) => {
    const { t } = useTranslation();
    return (
      <Box gap={"xl"}>
        <VBox>
          <strong>{t("dbot.matters")}</strong>
          {decisions.map((decisions) =>
            decisions.decisions.map((decisionType) => (
              <div key={decisionType.decisionType}>
                <Checkbox
                  checked={decisionType.isSelected}
                  label={<DecisionLabel decisionType={decisionType.decisionType} />}
                  onChange={() => toggleDecision(decisionType.decisionType)}
                />
              </div>
            )),
          )}
        </VBox>
        <VBox>
          <strong>{t("dbot.companies")}</strong>
          <Box>
            <LinkButton label={t("select-all")} onClick={action?.toggleAllCompanies} /> /{" "}
            <LinkButton label={t("select-none")} onClick={action?.deselectAllCompanies} />
          </Box>
          {companies
            .filter((c) => c.accessType === AccessType.ADMIN)
            .map(({ name, organisationNumber }) => (
              <Checkbox
                label={name}
                key={organisationNumber}
                value={organisationNumber}
                checked={action?.isCompanySelected(organisationNumber)}
                onChange={() => action?.toggleCompanyInAddDialog(organisationNumber)}
              />
            ))}
        </VBox>
      </Box>
    );
  },
);

const AddCompanyToPortfolioDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: AddCompanyToPortfolioActions }) => {
    const { t } = useTranslation();
    const isSubmitDisabled =
      state.selectedCompanies.length === 0 || state.availableCompanies.length === 0;
    return state.portfolioResponse.portfolio ? (
      <Dialog
        title={t("portfolio.add-companies", { name: state.portfolioResponse.portfolio.name })}
        onCancel={actions.closeAddCompanyDialog}
        onSubmit={actions.saveCompaniesInPortfolio}
        isSubmitting={isPending(state.addCompaniesResult)}
        submitResult={state.addCompaniesResult}
        isSubmitDisabled={isSubmitDisabled}
      >
        <VBox gap={"sm"}>
          <div>{t("portfolio.add-companies.description")}</div>
          {state.availableCompanies
            .filter((c) => isBlank(c.portfolioId))
            .map(({ name, organisationNumber }) => (
              <Checkbox
                checked={actions.isCompanySelected(organisationNumber)}
                label={name}
                key={organisationNumber}
                value={organisationNumber}
                onChange={() => actions.toggleCompanyInAddDialog(organisationNumber)}
                disabled={isPending(state.addCompaniesResult)}
              />
            ))}
          {state.availableCompanies
            .filter((c) => isNotBlank(c.portfolioId))
            .map(({ name, organisationNumber }) => (
              <Checkbox label={name} key={organisationNumber} value={organisationNumber} disabled />
            ))}
        </VBox>
      </Dialog>
    ) : null;
  },
);

const RemoveCompanyDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.remove-from-portfolio.title", {
          company: state.removeCompanyFromPortfolio?.name,
          portfolio: state.portfolioResponse?.portfolio?.name,
        })}
        onCancel={actions.portfolioActions.closeRemoveCompanyDialog}
        onSubmit={actions.portfolioActions.removeFromPortfolio}
        isSubmitting={isPending(state.removeFromPortfolioResult)}
        submitResult={state.removeFromPortfolioResult}
        submitLabel={t("continue")}
      >
        <div>
          {t("portfolio.remove-from-portfolio.description", {
            company: state.removeCompanyFromPortfolio?.name,
          })}
          <br />
          <br />
        </div>
        <div>{t("sure-you-want-to-proceed")}</div>
      </Dialog>
    );
  },
);

const MoveCompanyToPortfolioDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.move-company-dialog.title", {
          company: state.moveToPortfolio?.name,
          portfolio: state.portfolioResponse?.portfolio?.name,
        })}
        onCancel={actions.portfolioActions.closeMoveCompanyToPortfolioDialog}
        onSubmit={actions.portfolioActions.moveCompanyToPortfolio}
        isSubmitting={isPending(state.moveToPortfolioResult)}
        submitResult={state.moveToPortfolioResult}
        submitLabel={t("continue")}
      >
        <div>
          <Autocomplete
            disabled={isPending(state.moveToPortfolioResult) || isOk(state.moveToPortfolioResult)}
            label={t("portfolio.move-company-dialog.label")}
            placeholder={t("archive.move-dialog.destination-folder.placeholder")}
            items={state.portfolioResponse.portfolios
              .filter((p) => p.id !== state.portfolioResponse.portfolio?.id)
              .toSorted((a, b) => a.name.localeCompare(b.name))}
            idFn={(entry) => entry.id}
            labelFn={(entry) => entry.name}
            onSelect={(entry) => actions.portfolioActions.setDestinationPortfolio(entry)}
          />
        </div>
        <br />
        <Alert type={"info"} message={t("portfolio.move-company-dialog.only-your-portfolios")} />
      </Dialog>
    );
  },
);

export const AskForPermissionActions: FC<{
  result?: Result<TMessage, TMessage>;
  closeAction: () => void;
  submitAction: (autoonboard: boolean) => void;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  isBatch?: boolean;
}> = observer(({ result, closeAction, submitAction, onboardingCandidatesResult, isBatch }) => {
  const { t } = useTranslation();
  const submitted = isOk(result);
  return (
    <Box justifyContent={"space-between"} alignItems={"baseline"}>
      <div>
        {!isProductionEnvironment() && !submitted && (
          <div className={"subtle"}>Demo only: Shift click for instant onboarding</div>
        )}
      </div>
      <Box>
        <Button variant={"secondary"} label={t("close")} onClick={closeAction} />
        {!submitted && (
          <Button
            variant={"primary"}
            label={t("askForPermissionDialog.sendRequest")}
            onClick={(e) => submitAction(e.shiftKey && !isProductionEnvironment())}
            disabled={isPending(result) || (!isOk(onboardingCandidatesResult) && !isBatch)}
            loading={isPending(result)}
          />
        )}
      </Box>
    </Box>
  );
});

export const NorwegianCompanySearchDialog: FC<{
  state: CompanySearchDialogState;
  actions: CompanySearchActions;
}> = observer(({ state, actions }) => {
  const { t } = useTranslation();
  const locale = useLanguage();
  return (
    <Dialog
      title={t(
        state.selectedCompany
          ? "registerCompany.company.addCompany.header"
          : "registerCompany.addCompany.header",
        { company: state.selectedCompany?.name ?? "" },
      )}
      submitResult={state.askForPermissionResult}
      isSubmitting={isPending(state.askForPermissionResult)}
      fullscreen={isMobileView()}
      onCancel={actions.closeNorwegianCompanySearch}
      actions={
        <AskForPermissionActions
          onboardingCandidatesResult={state.onboardingCandidatesResult}
          result={state.askForPermissionResult}
          closeAction={actions.closeNorwegianCompanySearch}
          submitAction={actions.sendAskForPermissionRequest}
        />
      }
      maxWidth={"md"}
    >
      <VBox gap={"md"}>
        {state.selectedCompany && state.sourceOrgNoName && state.ownershipInPercent && (
          <div>
            {t("registerCompany.company.addCompany.ownsdescription", {
              source: state.sourceOrgNoName,
              percent: formatPercent(locale, state.ownershipInPercent),
              target: state.selectedCompany?.name,
            })}
          </div>
        )}
        {!isOk(state.askForPermissionResult) && !state.selectedCompany && (
          <>
            <p>{t("registerCompany.addCompany.body")}</p>
            <Autocomplete
              placeholder={t("registerCompany.searchLabel")}
              idFn={(i) => i.organizationnumber}
              items={isOk(state.searchResult) ? state.searchResult.data : []}
              inputValue={state.companyNameSearchField.inputValue}
              labelFn={(company) => company.name}
              onSelect={(company) => actions.selectCompany(company)}
              onInputValueChange={(query) => actions.searchForCompany(query)}
              onClear={() => state.companyNameSearchField.clear()}
              disabled={state.disableSearch}
            />
          </>
        )}
        {state.selectedCompany && (
          <AskForPermissionForm
            company={state.selectedCompany.name}
            askForPermissionResult={state.askForPermissionResult}
            receiverEmailAddress={state.receiverEmailAddress}
            onboardingCandidatesResult={state.onboardingCandidatesResult}
            companyRegisterKey={getCompanyRegisterKey(state.selectedCompany)}
          />
        )}
      </VBox>
    </Dialog>
  );
});

export const SwedishCompanySearchDialog: FC<{
  state: CompanySearchDialogState;
  actions: CompanySearchActions;
}> = observer(({ state, actions }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("portfolio.search-bolagsverket.title")}
      actions={
        <AskForPermissionActions
          result={state.askForPermissionResult}
          onboardingCandidatesResult={state.onboardingCandidatesResult}
          closeAction={actions.closeSwedishCompanySearch}
          submitAction={actions.sendAskForPermissionRequest}
        />
      }
      submitResult={state.askForPermissionResult}
      isSubmitting={isPending(state.askForPermissionResult)}
      cancelLabel={t("close")}
      fullscreen={isMobileView()}
      maxWidth={"md"}
    >
      {!isOk(state.askForPermissionResult) && (
        <VBox>
          <Box paddingBottom={"lg"}>{t("portfolio.search-bolagsverket.description")}</Box>
          <Box vertical={isMobileView()}>
            <MTextInput
              placeholder={t("portfolio.search-bolagsverket.search-placeholder")}
              model={state.searchField}
            />
            <div>
              <Button
                variant={"secondary"}
                type={"submit"}
                label={t("search")}
                loading={isPending(state.searchResult)}
                onClick={actions.searchSwedishBusinessRegister}
              />
            </div>
          </Box>
        </VBox>
      )}
      <VBox>
        {isOk(state.searchResult) && state.isRenderSearchResult && (
          <>
            <VBox padding>
              {t("portfolio.search-bolagsverket.result-count", {
                count: state.searchResult.data.length,
              })}
            </VBox>

            {state.searchResult.data.map((it) => {
              return (
                <SearchResultTableRow
                  key={it.name + it.organizationnumber}
                  company={it}
                  onSelect={() => actions.selectSwedishCompany(it)}
                />
              );
            })}
          </>
        )}
        {state.tooManyHits && <div>{t("portfolio.search-bolagsverket.too-many-hits")}</div>}
        {isOk(state.searchResult) && state.searchResult.data.length === 0 && (
          <div>
            {t("portfolio.search-bolagsverket.no-hits", {
              searchQuery: state.searchField.inputValue,
            })}
          </div>
        )}
        {state.selectedCompany && (
          <AskForPermissionForm
            company={state.selectedCompany.name}
            askForPermissionResult={state.askForPermissionResult}
            receiverEmailAddress={state.receiverEmailAddress}
            onboardingCandidatesResult={state.onboardingCandidatesResult}
            companyRegisterKey={getCompanyRegisterKey(state.selectedCompany)}
          />
        )}
      </VBox>
    </Dialog>
  );
});

function getCompanyRegisterKey(company?: SuggestedCompany) {
  if (company?.sourceCountry === "SE") {
    return "askForPermissionDialog.bolagsverket";
  } else {
    return "askForPermissionDialog.brreg";
  }
}

export const PortfolioCompaniesView = observer(
  ({
    state,
    alertStore,
    showSigningCompleteDialog,
    closeSigningCompleteDialog,
    companyName,
    userPrefs,
    userSettings,
    showFollowUpAlert,
    portfolio,
  }: {
    state: PreparedPageState;
    alertStore: AlertMessageStore;
    showSigningCompleteDialog: boolean;
    closeSigningCompleteDialog: () => void;
    companyName: string;
    userPrefs: UserPrefs;
    userSettings: UserSettings | null;
    showFollowUpAlert: boolean;
    portfolio: SelectedPortfolioOption;
  }) => {
    const renderBody = () => {
      switch (true) {
        case isPreparedPageState(state, "invalid-portfolioid"):
          return <InvalidPortfolioView />;
        case isPreparedPageState(state, "loading-portfolio"):
          return <LoadingIndicator fullscreen />;
        case isPreparedPageState(state, "ready"):
          return <PortfolioCompaniesReadyView {...state} userSettings={userSettings} />;
      }
    };
    const appBar = useAppBar({
      portfolioHeader: "companies",
      isPortfolioPage: true,
      selectedPortfolioOption: portfolio,
      openPortfolioSettings:
        isPreparedPageState(state, "ready") &&
        state.state.activeHeader === "follow-up" &&
        state.state.portfolio.type === "portfolio-instance"
          ? state.actions.openPortfolioSettings
          : undefined,
    });

    const { t } = useTranslation();
    const subHeaderElement: ReactElement | undefined =
      isPreparedPageState(state, "ready") && state.state.portfolio.type !== "portfolio-instance" ? (
        <Box bg>
          <SigningCompleteDialog
            companyName={companyName}
            showSigningCompleteDialog={showSigningCompleteDialog}
            closeSigningCompleteDialog={closeSigningCompleteDialog}
          />
          <VBox maxWidth="xl" container marginTop="lg">
            {showFollowUpAlert && !isAlertDismissed(userPrefs, "followUp") && (
              <Box justifyContent={"space-between"} alignItems={"center"} gap={"lg"} marginBottom>
                <Markdown value={t("alerts.followUp")} disableLastMargin />
                <CloseButton onClick={dismissAlert(userPrefs, "followUp")} />
              </Box>
            )}
            <Box justifyContent={"space-between"} alignItems={"center"}>
              <h4 data-testid={"portfolio-header"}>{t("header.portfolio")}</h4>
              <Box gap="xl" vertical={isMobileView()}>
                <DropdownMenu
                  button={{
                    type: "link",
                    icon: "plus",
                    label: t("portfolio.welcome.add-company"),
                  }}
                  items={[
                    {
                      type: "item",
                      label: t("norway"),
                      onSelect: state.actions.companySearchActions.openNorwegianCompanySearch,
                    },
                    {
                      type: "item",
                      label: t("sweden"),
                      onSelect: state.actions.companySearchActions.openSwedishCompanySearch,
                    },
                  ]}
                />
                {!isMobileView() && (
                  <PortfolioActionsDropdown state={state.state} actions={state.actions} />
                )}
              </Box>
            </Box>
            <div>
              <Tabs tabs={state.state.companiesTabs} />
            </div>
          </VBox>
        </Box>
      ) : undefined;

    return (
      <FullScreenLayout
        alertStore={alertStore}
        appBar={<AppBar data={appBar} disableDropShadow subHeaderElement={subHeaderElement} />}
        centerPanel={renderBody()}
        disableAppBarShadow
      />
    );
  },
);

export const PortfolioCompaniesReadyView = observer(
  ({
    state,
    actions,
    userSettings,
  }: {
    state: ReadyState;
    actions: PortfolioPageActions;
    userSettings: UserSettings | null;
  }) => {
    return (
      <>
        {state.confirmDissolvePortfolio && (
          <ConfirmDissolveDialog
            state={state}
            actions={actions}
            contactsAdminsUrl={extractPortfolioContactsAdminUrl(state.portfolio)}
          />
        )}
        {!state.confirmDissolvePortfolio && state.isPortfolioSettingsOpen && (
          <PortfolioSettingsDialog
            state={state}
            actions={actions}
            contactsAdminsUrl={extractPortfolioContactsAdminUrl(state.portfolio)}
          />
        )}
        {state.norwegianCompanySearchDialogState && (
          <NorwegianCompanySearchDialog
            state={state.norwegianCompanySearchDialogState}
            actions={actions.companySearchActions}
          />
        )}
        {state.swedishCompanySearchDialogState && (
          <SwedishCompanySearchDialog
            state={state.swedishCompanySearchDialogState}
            actions={actions.companySearchActions}
          />
        )}
        <CompaniesView state={state} actions={actions} userSettings={userSettings} />
        {hasAdminAccess(state.portfolioResponse) && state.userCompaniesInPortfolio.length > 0 && (
          <ContactFooter />
        )}
      </>
    );
  },
);

export const PendingInvitationDetailsDialog: FC<{
  invitationRequestResult: Result<InvitationRequest>;
  onClose: () => void;
}> = observer(({ invitationRequestResult, onClose }) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  return (
    <Dialog
      title={t("pendingInvitationDialog.title")}
      cancelLabel={t("close")}
      onCancel={onClose}
      isSubmitDisabled={false}
    >
      <div>
        <div>
          {isPending(invitationRequestResult) ? (
            <LoadingIndicator />
          ) : isOk(invitationRequestResult) ? (
            t("pendingInvitationDialog.description", {
              receiver: invitationRequestResult.data.recipientEmail,
              dateTime: formatDate(invitationRequestResult.data.requestedAt),
            })
          ) : (
            t("pendingInvitationDialog.description.fallback")
          )}
        </div>
        <br />
      </div>
    </Dialog>
  );
});

export const FilterForNotConnectedCompanies: FC<{
  state: ReadyState;
  actions: PortfolioPageActions;
}> = observer(({ state, actions }) => {
  const { t } = useTranslation();
  return state.companiesTab === "not-connected" ? (
    <>
      <Box justifyContent={"space-between"}>
        <Box>
          <Box>
            <MTextInput
              placeholder={t("search")}
              icon={"magnifying-glass"}
              model={state.portfolioSearchModel}
              onChange={(e) => {
                state.portfolioSearchModel.setAndCommitInputValue(e);
                actions.portfolioActions.filterWithText();
              }}
            />
          </Box>
          <Box>
            <MSelect
              model={state.notConnected.rolesFilter}
              onSelect={actions.notConnected.filterWithRole}
            />
          </Box>
        </Box>
        {!isProductionEnvironment() && (
          <Button
            variant={"secondary"}
            icon="plugs"
            label={t("connect-all", {
              count: state.notConnected.companies.filter((c) => c.accessType !== AccessType.DRAFT)
                .length,
            })}
            size={"sm"}
            disabled={
              state.portfolioResponse.subsidiaries.length === 0 ||
              state.notConnected.companies.length === 0
            }
            onClick={actions.notConnected.toggleConnectAllDialog}
          />
        )}
      </Box>
      {state.notConnected.showConnectAllDialog && (
        <ConnectAllDialog state={state.notConnected} actions={actions.notConnected} />
      )}
    </>
  ) : null;
});

const ConnectAllDialog = observer(
  ({
    state,
    actions,
  }: {
    state: NotConnectedTabState;
    actions: ActionsForNotConnectedCompanies;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.connect-all.add-companies")}
        actions={
          <AskForPermissionActions
            isBatch
            result={state.connectAllCompaniesResult}
            submitAction={actions.submitConnectAll}
            closeAction={actions.toggleConnectAllDialog}
          />
        }
      >
        <div>{t("portfolio.connect-all.add-companies-description")}</div>
        <Box marginTop>
          <LinkButton label={t("select-all")} onClick={actions.selectAll} /> /{" "}
          <LinkButton label={t("select-none")} onClick={actions.selectNone} />
        </Box>

        {state.subsidiaries.map((c) => (
          <>
            <Grid key={c.subsidiary.organizationNumber} cols={2} marginTop marginBottom={"sm"}>
              <VBox>
                <Checkbox
                  label={
                    <Box>
                      {c.subsidiary.name}{" "}
                      <div className="subtle">({c.subsidiary.organizationNumber})</div>
                    </Box>
                  }
                  checked={c.checked}
                  onChange={() => actions.toggleCheckedSubsidiary(c)}
                />
                {c.subsidiary.ceoName && (
                  <div>
                    {c.subsidiary.ceoName} ({t("roles.ceo")})
                  </div>
                )}
                {c.subsidiary.chairPersonName && (
                  <div>
                    {c.subsidiary.chairPersonName} ({t("roles.chairperson")})
                  </div>
                )}
              </VBox>
              <VBox>
                {t("askForPermissionDialog.email-to-ceo-or-chairperson")}
                <MTextInput model={c.receiverEmail} />
              </VBox>
            </Grid>
            <hr />
          </>
        ))}
      </Dialog>
    );
  },
);

export const EmptyPortfolioView = observer(
  ({
    portfolioName,
    openAddCompanyDialog,
    openPortfolioSettings,
    confirmDissolvePortfolio,
  }: {
    portfolioName?: string;
    openAddCompanyDialog: () => void;
    openPortfolioSettings: () => void;
    confirmDissolvePortfolio: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox grow={"golden"} justifyContent={"center"} fg={"subtle"}>
        <EmptyState
          illustration={"briefcase"}
          title={portfolioName ?? ""}
          description={t("portfolio.empty")}
          actions={
            <VBox gap={"xl"}>
              <Button
                variant={"secondary"}
                icon={"plus"}
                onClick={openAddCompanyDialog}
                label={t("portfolio.welcome.add-company")}
              />
              <LinkButton
                icon={"edit"}
                label={t("portfolio.options.menu.rename-portfolio")}
                onClick={openPortfolioSettings}
              />
              <LinkButton
                icon={"trash"}
                color={"error"}
                label={t("portfolio.dissolve-portfolio")}
                onClick={confirmDissolvePortfolio}
              />
            </VBox>
          }
        />
      </VBox>
    );
  },
);

export function ContactFooter() {
  const { t } = useTranslation();
  return (
    <Container maxWidth={"sm"} marginY={"lg"}>
      <VBox textAlign={"center"}>
        <Markdown value={t("portfolio.footer")} />
      </VBox>
    </Container>
  );
}

function openARDocumentation() {
  window.open(altinnMainDocsUrl);
}

const PortfolioActionsDropdown = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const decisionsByCategory = getDecisionsByCategory({
      isBatchProject: true,
      checkedDecisions: state.checkedDecisions,
      paidSubscription: {
        organisationNumber: "",
        subscriptionStatus: SubscriptionStatus.ACTIVE,
        subscriptionType: SubscriptionType.PRO,
        isActive: true,
      },
    });
    const companiesWithAdminAccess = state.userCompaniesInPortfolio.filter(
      (a) => a.accessType === AccessType.ADMIN,
    );
    const hasMultipleCompanies: boolean = companiesWithAdminAccess.length > 1;
    const hasAdminAccess = companiesWithAdminAccess.length > 0;

    return hasAdminAccess ? (
      <DropdownMenu
        button={{
          type: "button",
          variant: "secondary",
          label: t("portfolio.actions"),
          loading: state.isLoadingCustomRole,
          onClick: actions.getCustomRoleItems,
        }}
        items={
          !state.isLoadingCustomRole
            ? [
                {
                  type: "label",
                  label: t("portfolio.actions.admin-in-companies", {
                    count: state.adminsCompanyCount,
                  }),
                },
                {
                  type: "item",
                  label: t("portfolio.invite-admin"),
                  icon: "user-plus",
                  onSelect: () => actions.openInviteAdministratorDialog(),
                },
                ...(state.customRoleItem?.customRoleItems
                  ?.filter((f) => f.companies.length > 1)
                  .map<DropdownMenuItemType>((roleItem) => ({
                    type: "item",
                    label: t("portfolio.actions.invite-custom-role", {
                      customRoleName: getRoleNameByLanguage(roleItem.role, locale),
                    }),
                    icon: "user-plus",
                    onSelect: () => actions.openInviteCustomRoleDialog(roleItem),
                  })) ?? []),
                {
                  type: "item",
                  label: t("norway"),
                  icon: "plus",
                  onSelect: actions.companySearchActions.openNorwegianCompanySearch,
                },
                {
                  type: "item",
                  label: t("sweden"),
                  icon: "plus",
                  onSelect: actions.companySearchActions.openSwedishCompanySearch,
                },
                {
                  type: "item",
                  label: t("portfolio.actions.create-new-portfolio"),
                  icon: "folder-plus",
                  onSelect: actions.openCreatePortfolioDialog,
                },
                {
                  type: "item",
                  label: t("portfolio.export-shareholder-registers"),
                  icon: "download",
                  onSelect: actions.openExportShareholdersDialog,
                },
                hasMultipleCompanies && {
                  type: "label",
                  label: t("portfolio.options.menu.start-portfolio-project"),
                },

                ...decisionsByCategory.flatMap((category) =>
                  category.decisions.map<DropdownMenuItemType | null>((type) =>
                    hasMultipleCompanies
                      ? {
                          type: "item",
                          label: <DecisionLabel decisionType={type.decisionType} />,
                          icon: "sparkle",
                          onSelect: () => {
                            actions.openCreateProjectDialog(type.decisionType);
                          },
                        }
                      : null,
                  ),
                ),
              ]
            : []
        }
      />
    ) : undefined;
  },
);
